/**
 * Customer service vocabulary (spec 2026-10-25-service-desk §2-§4).
 *
 * Persisted as VARCHAR and validated against these lists in code, so adding a
 * value needs no migration. Every status change goes through the state machine
 * in the service-desk module, never a free-text write.
 */

export const CASE_STATUSES = [
  'NEW',
  'IN_PROGRESS',
  'WAITING_CUSTOMER',
  'ESCALATED',
  'RESOLVED',
  'CLOSED',
] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

/**
 * The case state machine (spec 2026-10-25-service-desk §2). Any open status may
 * escalate or resolve; RESOLVED can be closed or reopened (a reason is required,
 * enforced by the API); CLOSED is final. One copy, used by the API to refuse an
 * illegal move and by the web to decide which buttons to offer.
 */
export const CASE_TRANSITIONS: Readonly<Record<CaseStatus, readonly CaseStatus[]>> = {
  NEW: ['IN_PROGRESS', 'WAITING_CUSTOMER', 'ESCALATED', 'RESOLVED'],
  IN_PROGRESS: ['WAITING_CUSTOMER', 'ESCALATED', 'RESOLVED'],
  WAITING_CUSTOMER: ['IN_PROGRESS', 'ESCALATED', 'RESOLVED'],
  ESCALATED: ['IN_PROGRESS', 'RESOLVED'],
  RESOLVED: ['CLOSED', 'IN_PROGRESS'],
  CLOSED: [],
};

export const CASE_CHANNELS = ['WALK_IN', 'PHONE', 'WHATSAPP', 'EMAIL', 'SOCIAL', 'WEB_FORM'] as const;
export type CaseChannel = (typeof CASE_CHANNELS)[number];

export const CASE_CATEGORIES = ['COMPLAINT', 'ENQUIRY', 'WARRANTY', 'REQUEST', 'FEEDBACK'] as const;
export type CaseCategory = (typeof CASE_CATEGORIES)[number];

export const CASE_SEVERITIES = ['LOW', 'MEDIUM', 'HIGH'] as const;
export type CaseSeverity = (typeof CASE_SEVERITIES)[number];

/** MEMBERSHIP is reserved: membership is not built yet. */
export const CASE_LINK_TYPES = ['SALE', 'JOB_CARD', 'DELIVERY', 'MEMBERSHIP'] as const;
export type CaseLinkType = (typeof CASE_LINK_TYPES)[number];

export const CASE_ACTIVITY_TYPES = ['NOTE', 'RESPONSE', 'CALL', 'WHATSAPP'] as const;
export type CaseActivityType = (typeof CASE_ACTIVITY_TYPES)[number];

export const FOLLOW_UP_TYPES = [
  'POST_DELIVERY',
  'POST_SERVICE',
  'SERVICE_REMINDER',
  'REACTIVATION',
  'UPGRADE',
] as const;
export type FollowUpType = (typeof FOLLOW_UP_TYPES)[number];

export const FOLLOW_UP_STATUSES = ['OPEN', 'DONE', 'CANCELLED'] as const;
export type FollowUpStatus = (typeof FOLLOW_UP_STATUSES)[number];

export const FOLLOW_UP_OUTCOMES = [
  'REACHED_HAPPY',
  'REACHED_ISSUE',
  'NO_ANSWER',
  'WRONG_NUMBER',
  'DECLINED',
] as const;
export type FollowUpOutcome = (typeof FOLLOW_UP_OUTCOMES)[number];

export const FOLLOW_UP_SOURCE_TYPES = ['DELIVERY', 'JOB_CARD', 'REMINDER', 'RETENTION'] as const;
export type FollowUpSourceType = (typeof FOLLOW_UP_SOURCE_TYPES)[number];

export const REMINDER_STATUSES = ['SCHEDULED', 'DUE', 'SENT', 'DISMISSED', 'SUPERSEDED'] as const;
export type ReminderStatus = (typeof REMINDER_STATUSES)[number];

export const REMINDER_RULE_CODES = ['FIRST_SERVICE', 'REGULAR'] as const;
export type ReminderRuleCode = (typeof REMINDER_RULE_CODES)[number];

function includes<T extends string>(list: readonly T[], value: string): value is T {
  return (list as readonly string[]).includes(value);
}

export const isCaseStatus = (v: string): v is CaseStatus => includes(CASE_STATUSES, v);
export const isCaseChannel = (v: string): v is CaseChannel => includes(CASE_CHANNELS, v);
export const isCaseCategory = (v: string): v is CaseCategory => includes(CASE_CATEGORIES, v);
export const isCaseSeverity = (v: string): v is CaseSeverity => includes(CASE_SEVERITIES, v);
export const isCaseLinkType = (v: string): v is CaseLinkType => includes(CASE_LINK_TYPES, v);
export const isCaseActivityType = (v: string): v is CaseActivityType => includes(CASE_ACTIVITY_TYPES, v);
export const isFollowUpType = (v: string): v is FollowUpType => includes(FOLLOW_UP_TYPES, v);
export const isFollowUpStatus = (v: string): v is FollowUpStatus => includes(FOLLOW_UP_STATUSES, v);
export const isFollowUpOutcome = (v: string): v is FollowUpOutcome => includes(FOLLOW_UP_OUTCOMES, v);
export const isReminderStatus = (v: string): v is ReminderStatus => includes(REMINDER_STATUSES, v);
export const isReminderRuleCode = (v: string): v is ReminderRuleCode => includes(REMINDER_RULE_CODES, v);
